import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Alert } from '../api/alerts';

interface AlertItemProps {
  alert: Alert;
  onDismiss?: (id: number) => Promise<void>;
  onConfirm?: (id: number) => Promise<void>;
}

const AlertItem: React.FC<AlertItemProps> = ({ alert, onDismiss, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Log pour debug production
  console.log('[AlertItem] Render:', {
    id: alert.id,
    title: alert.title,
    status: alert.status,
    statusType: typeof alert.status,
    isActive: alert.status === 'ACTIVE',
    hasHandlers: { onDismiss: !!onDismiss, onConfirm: !!onConfirm }
  });

  // Normalisation du statut (évite les soucis de casse / valeurs inconnues)
  const statusNorm = (alert.status || '').toString().toUpperCase();
  const isActionable = ['ACTIVE', 'PENDING', 'EXPIRED', 'NEW', ''].includes(statusNorm);

  // Déterminer les styles selon la priorité
  let borderClass = 'border-l-4 border-l-base-300';
  let badgeClass = 'badge-ghost';

  switch (alert.priority) {
    case 'URGENT':
      borderClass = 'border-l-4 border-l-error';
      badgeClass = 'badge-error';
      break;
    case 'HIGH':
      borderClass = 'border-l-4 border-l-warning';
      badgeClass = 'badge-warning';
      break;
    case 'MEDIUM':
      borderClass = 'border-l-4 border-l-info';
      badgeClass = 'badge-info';
      break;
    case 'LOW':
      borderClass = 'border-l-4 border-l-success';
      badgeClass = 'badge-success';
      break;
  }

  // Badge de statut
  const statusBadge = {
    ACTIVE: 'badge-primary',
    PENDING: 'badge-primary',
    NEW: 'badge-primary',
    EXPIRED: 'badge-warning',
    SNOOZED: 'badge-warning',
    DISMISSED: 'badge-neutral',
    CONFIRMED: 'badge-info',
    RESOLVED: 'badge-success',
  }[statusNorm] || 'badge-ghost';

  // Rediriger vers la page Fin de Contrat pour planifier un rappel
  const handleRedirectReminder = () => {
    const params = new URLSearchParams();
    params.set('alertId', String(alert.id));
    if ((alert as any).employee_id) params.set('employeeId', String((alert as any).employee_id));
    if ((alert as any).contract_id) params.set('contractId', String((alert as any).contract_id));
    navigate(`/fin-contrat?${params.toString()}`);
  };

  const handleDismiss = async () => {
    if (!onDismiss) return;
    setLoading(true);
    try {
      await onDismiss(alert.id);
    } catch (err) {
      console.error('Erreur dismiss:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!onConfirm) return;
    setLoading(true);
    try {
      await onConfirm(alert.id);
    } catch (err) {
      console.error('Erreur confirm:', err);
    } finally {
      setLoading(false);
    }
  };

  // Formater les jours restants
  const daysRemainingText = () => {
    if (alert.days_remaining === null) return null;
    
    if (alert.days_remaining < 0) {
      return (
        <span className="text-error font-semibold">
          Expiré depuis {Math.abs(alert.days_remaining)} jour(s)
        </span>
      );
    } else if (alert.days_remaining === 0) {
      return <span className="text-error font-semibold">Expire aujourd'hui !</span>;
    } else if (alert.days_remaining <= 7) {
      return (
        <span className="text-warning font-semibold">
          {alert.days_remaining} jour(s) restant(s)
        </span>
      );
    } else {
      return <span className="text-base-content/70">{alert.days_remaining} jours</span>;
    }
  };

  return (
    <div className={`p-4 mb-3 ${alert.status !== 'ACTIVE' ? 'bg-base-200/50' : 'bg-base-200'} rounded-lg shadow-sm ${borderClass} transition-all duration-200 hover:shadow-md`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${alert.status !== 'ACTIVE' ? 'text-base-content/60' : 'text-base-content'}`}>
            {alert.title}
          </p>
          <p className="text-xs text-base-content/70 mt-1 line-clamp-2">{alert.message}</p>
          
          {alert.employee_name && (
            <p className="text-xs text-base-content/50 mt-1 flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {alert.employee_name}
            </p>
          )}

          {alert.target_date && (
            <p className="text-xs mt-1 flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {new Date(alert.target_date).toLocaleDateString('fr-FR')}
              {alert.days_remaining !== null && <> • {daysRemainingText()}</>}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 ml-2">
          <span className={`badge badge-sm ${badgeClass} whitespace-nowrap`}>
            {alert.priority_display}
          </span>
          <span className={`badge badge-xs ${statusBadge}`}>{alert.status_display}</span>
          {alert.is_urgent && (
            <span className="badge badge-xs badge-error animate-pulse">URGENT</span>
          )}
        </div>
      </div>

      {/* Boutons d'action - seulement pour les alertes actives */}
      {isActionable && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-base-300">
          {/* Bouton Confirmer */}
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="btn btn-xs btn-success gap-1"
            title="Confirmer la prise de connaissance"
          >
            {loading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            Confirmer
          </button>

          {/* Bouton Me rappeler -> redirection Fin de Contrat */}
          <button
            onClick={handleRedirectReminder}
            disabled={loading}
            className="btn btn-xs btn-warning gap-1"
            title="Planifier un rappel sur Fin de Contrat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Me rappeler
          </button>

          {/* Bouton Ignorer */}
          <button
            onClick={handleDismiss}
            disabled={loading}
            className="btn btn-xs btn-ghost gap-1"
            title="Ignorer cette alerte"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Ignorer
          </button>
        </div>
      )}

      {/* Message pour les alertes confirmées */}
      {alert.status === 'CONFIRMED' && alert.confirmed_at && (
        <div className="mt-2 text-xs text-success flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Confirmé le {new Date(alert.confirmed_at).toLocaleDateString('fr-FR')}
        </div>
      )}
    </div>
  );
};

export default AlertItem;
